import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { showErrorToast, showSuccessToast } from 'components/Toast';
import BaseInput from 'components/Inputs/BaseInput';
import SolidButton from 'components/SolidButton';

import { REQUIRED_RULE } from 'utils/constants';
import GeneralApi from 'utils/generalApi';
import { ISerie } from 'utils/interfaces';
import { useAuth } from 'contexts/Auth';

export default function SerieModalContent({
  title,
  editData,
  onClose,
  onSuccess,
}: {
  title: string;
  editData?: ISerie;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const {
    register,
    watch,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<any>({
    defaultValues: editData || {
      doc_type: 'F',
    },
  });
  const history = useNavigate();
  const auth = useAuth();

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const [loading, setLoading] = useState(false);

  const submitAction = async (data: any) => {
    setLoading(true);
    let result: any = await (editData
      ? generalApi.put('/serie', {
          ...data,
          serieId: editData._id,
        })
      : generalApi.post('/serie', data));
    setLoading(false);
    if (!result.success) return showErrorToast(result.data.err);
    showSuccessToast(editData ? 'Cambios guardados' : 'Serie creada');
    onSuccess();
  };

  const buildBaseInput = (
    label: string,
    type: React.HTMLInputTypeAttribute,
    name: string,
    rules: any,
    props: any
  ) => {
    return (
      <BaseInput
        label={label}
        type={type}
        name={name}
        register={register}
        control={control}
        watch={watch}
        errors={errors}
        rules={rules}
        {...props}
      />
    );
  };

  return (
    <form onSubmit={handleSubmit(submitAction)}>
      <div className="bg-primary-600 rounded-t-lg px-4 pb-4 sm:flex sm:items-start sm:p-6 sm:py-4">
        <div className="mt-3 text-center sm:mt-0 sm:text-left">
          <h3 className="text-lg font-medium leading-6 text-white" id="modal-headline">
            {title || 'Confirmar acción'}
          </h3>
        </div>
      </div>
      <div onSubmit={handleSubmit(submitAction)} className="space-y-4 px-4 py-5 sm:p-6">
        {buildBaseInput('Tipo', 'select', 'doc_type', REQUIRED_RULE, {
          options: [
            { value: 'B', name: 'Boleta' },
            { value: 'F', name: 'Factura' },
            { value: 'T', name: 'Ticket' },
            { value: 'C', name: 'Nota de Crédito' },
            { value: 'D', name: 'Nota de Débito' },
          ],
        })}
        {buildBaseInput('Serie', 'text', 'serie', REQUIRED_RULE, {})}
        {buildBaseInput('Número', 'number', 'number', REQUIRED_RULE, {})}
        {buildBaseInput('Comentario', 'text', 'comment', REQUIRED_RULE, {})}
      </div>
      <div className="bg-gray-50 rounded-b-lg px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
        <SolidButton
          className="w-auto sm:ml-3"
          isLoading={loading}
          onClick={() => handleSubmit(submitAction)}
        >
          {editData ? 'Guardar cambios' : 'Crear'}
        </SolidButton>
        <button
          type="button"
          onClick={onClose}
          className="focus:ring-primary-500 mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}
